/**
 * Combat data types (owner: combat-builder). Extends the integrator contract `BattleConfig` with the
 * fields the tutorial battle needs; every extension is optional-by-design so a plain BattleConfig is
 * still a valid input for the engine (rules/ai/sprites fall back to defaults).
 */
import type { BattleActionConfig, BattleConfig, BattleOutcome, DialogueData } from '@/core/types/contracts';

/**
 * Script hooks. The contract's set plus `playerSkillMiss`: the player's skill missed — fires after the
 * miss resolves instead of `playerSkill`/`playerMiss`, so praise never precedes 刺空了.
 */
export type ScriptOn = BattleConfig['script'][number]['on'] | 'playerSkillMiss';

/** The tutor's scripted moves (赵呈博 pattern: 试探 → 虚招 → 真剑 → 收势 → …). */
export type TutorMove = 'probe' | 'feint' | 'strike' | 'praise';

export interface BattleRules {
  /** The bout cannot end on goal/rounds before this many rounds were fought (downed still ends it). */
  minRounds: number;
  /** Party member hp <= floor(maxHp * lowHpRatio) → tutor stops the bout ('downed'). */
  lowHpRatio: number;
  /** Damage multiplier while guarding. */
  guardDamageMul: number;
  /** 观察 does not consume the turn (once per round). */
  observeIsFree: boolean;
  /** Damage roll is ±variance (inclusive). */
  variance: number;
  /** Enemy damage is clamped so a party member always keeps >= 1 hp. */
  enemiesNeverKill: boolean;
}

export interface TutorMoveSpec { action: string; label: string; comment: string }
export interface TutorAiConfig { id: string; pattern: TutorMove[]; moves: Record<TutorMove, TutorMoveSpec> }

export interface BattleScriptEntry {
  on: ScriptOn;
  round?: number;
  dialogue: string;
  /** For `on: 'end'`: only used for this outcome (an entry without outcome is the fallback). */
  outcome?: BattleOutcome;
  /** Fire at most once (default true for everything except `round`). */
  once?: boolean;
}

/** The five animations every fighter has; a sprite spec may add extra strips (observe, praise, …) by name. */
export type BattleAnimName = 'idle' | 'attack' | 'skill' | 'guard' | 'hit';
export interface BattleAnimSpec {
  frames: number;
  fps: number;
  loop?: boolean;
  /** Atlas strip the frames come from when it differs from the anim name (`<source>-<n>`), e.g. zhao-chengbo skill → parry. */
  source?: string;
}
export interface BattleDevPalette { robe: string; sash: string; skin: string; hair: string; blade: string; beard?: string }
export interface BattleSpriteSpec {
  /** Texture key (`battle-<id>` in public/assets/manifest.json). Atlas frames are named `<strip>-<n>`. */
  texture: string;
  frameW: number;
  frameH: number;
  facing: 'left' | 'right';
  /** idle/attack/skill/guard/hit are required; any other key is an extra strip playable by name. */
  anims: Record<BattleAnimName, BattleAnimSpec> & Partial<Record<string, BattleAnimSpec>>;
  /** Dev placeholder palette, used only when the texture is missing. */
  dev: BattleDevPalette;
}

export interface BattleActionSpec extends BattleActionConfig {
  /** 'feint': looks like an attack, never deals damage. 'rest': tutor lowers the sword, gives the target SP. */
  effect?: 'feint' | 'rest';
  /** SP granted to the target by `effect: 'rest'`. */
  targetSp?: number;
  /** Short menu hint (e.g. cost). */
  hint?: string;
}

/** `script` is widened (ScriptOn adds `playerSkillMiss`), hence the Omit; every other field is the contract's. */
export interface SwordTrainingConfig extends Omit<BattleConfig, 'script'> {
  title: string;
  actions: BattleActionSpec[];
  script: BattleScriptEntry[];
  rules: BattleRules;
  ai: TutorAiConfig[];
  /** Tutor/student lines, registered with the dialogue module and rendered by the battle speech box. */
  dialogues: DialogueData[];
  sprites: Record<string, BattleSpriteSpec>;
  ambience?: string[];
}

export const DEFAULT_RULES: BattleRules = {
  minRounds: 3,
  lowHpRatio: 0.25,
  guardDamageMul: 0.5,
  observeIsFree: true,
  variance: 2,
  enemiesNeverKill: true,
};

/** Data handed to the Battle scene by runBattle. */
export interface BattleLaunchData {
  configId: string;
  callerKey: string;
  seed: number;
  onDone: (outcome: BattleOutcome) => void;
  /** Showcase mode: no caller to resume. */
  standalone?: boolean;
}
